import React, { useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useQuoteEditor } from "../context/QuoteEditorContext"
import { hasLiveChildren, isBreakdown } from "../helpers/treeMath"

const modifierKey = () =>
  (typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "")) ? "⌘" : "Ctrl+"

const hasCollapsed = nodes =>
  (nodes || []).some(n => {
    if (isBreakdown(n) && !hasLiveChildren(n)) return false
    if (n._expanded === false) return true
    return hasCollapsed(n.children) || hasCollapsed(n.components)
  })

const ColHead = ({ onOpenSearch }) => {
  const { state, dispatch } = useQuoteEditor()
  const anyCollapsed = useMemo(() => hasCollapsed(state.sections), [state.sections])

  const toggleAll = () => {
    dispatch({ type: "SET_ALL_EXPANDED", payload: { expanded: anyCollapsed } })
  }

  const addGroupAtTop = () => {
    dispatch({
      type: "ADD_SECTION",
      payload: {
        type:        "group",
        description: "",
        children:    [],
        _expanded:   true,
        insertAtIndex: 0,
      },
    })
  }

  return (
    <div className="qb-grid items-center px-3 py-2.5 bg-slate-100 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold shrink-0">
      <div className="col-start-1 flex items-center gap-1 min-w-0">
        <button
          type="button"
          onClick={toggleAll}
          aria-label={anyCollapsed
            ? I18n.t("quotes.builder.col_head.expand_all")
            : I18n.t("quotes.builder.col_head.collapse_all")}
          title={anyCollapsed
            ? I18n.t("quotes.builder.col_head.expand_all")
            : I18n.t("quotes.builder.col_head.collapse_all")}
          className="inline-grid place-items-center w-5 h-5 rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
               className={`transition-transform ${anyCollapsed ? "" : "rotate-90"}`}>
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
        <button
          type      = "button"
          onClick   = {addGroupAtTop}
          title     = {I18n.t("quotes.builder.col_head.add_group_at_top")}
          className = "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold text-slate-500 hover:bg-slate-200 hover:text-slate-700 whitespace-nowrap"
        >
          <FontAwesomeIcon icon="plus" />
          {I18n.t("quotes.builder.col_head.add_group")}
        </button>
      </div>
      <div className="col-start-2">{I18n.t("quotes.builder.col_head.description")}</div>
      <div className="col-start-3 text-right justify-self-end">{I18n.t("quotes.builder.col_head.qty")}</div>
      <div className="col-start-4 text-center justify-self-center">{I18n.t("quotes.builder.col_head.unit")}</div>
      <div className="qb-col-purchase col-start-5 text-right justify-self-end">{I18n.t("quotes.builder.col_head.purchase_price")}</div>
      <div
        className = "qb-col-coef col-start-6 text-right justify-self-end cursor-help"
        title     = {I18n.t("quotes.builder.col_head.coef_tooltip")}
      >
        {I18n.t("quotes.builder.col_head.coef")}
      </div>
      <div className="col-start-7 text-right justify-self-end">{I18n.t("quotes.builder.col_head.sale_price")}</div>
      <div className="qb-col-margin col-start-8 text-right justify-self-end">{I18n.t("quotes.builder.col_head.margin")}</div>
      <div className="col-start-9 text-center justify-self-center">{I18n.t("quotes.builder.col_head.vat")}</div>
      <div className="col-start-10 text-right justify-self-end">{I18n.t("quotes.builder.col_head.total_excl_tax")}</div>
      <div className="col-start-11 flex items-center justify-end">
        {onOpenSearch && (
          <button
            type      = "button"
            onClick   = {onOpenSearch}
            title     = {`${I18n.t("quotes.builder.search.open")} (${modifierKey()}F)`}
            aria-label= {I18n.t("quotes.builder.search.open")}
            className = "inline-grid place-items-center w-5 h-5 rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700"
          >
            <FontAwesomeIcon icon="magnifying-glass" className="!w-2.5" />
          </button>
        )}
      </div>
    </div>
  )
}

export default ColHead
